import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import useNonce from "@hooks/useNonce";
import scrollToTheTop from "@lib/scrollToTheTop";
import debounce from "@utils/debounce";

import { OrderFields, ORDER_MESSAGE_ZONE } from "../../../constants";
import loadOffers from "../../../services/loadOffers";
import type { TCoupon, TOffer, TPackageInfoFieldErrors } from "../../../types";
import useOrderFieldsLoading from "../../useOrderFieldsLoading";

const debouncedOffersLoading = debounce((func) => func(), 200);

export type TUpdateOffersParams = {
  countryFrom?: string;
  countryTo?: string;
  deliveryMethod?: string;
  packageSizeTitle?: string;
  promoCode?: string;
  senderZipCode?: string;
  receiverZipCode?: string;
  affiliateId?: string;
  shipmentId?: string;
  senderEmail?: string;
  senderPhoneNumber?: string;
  offerGroupId?: string;
};

type TUpdateOffersData = {
  offers: TOffer[];
  coupon: TCoupon | null;
  errorMessage: string | null;
  fieldErrors: TPackageInfoFieldErrors | null;
};

type TUpdateOffersReturn = {
  offers: TOffer[];
  coupon: TCoupon | null;
  errorMessage: string | null;
  fieldErrors: TPackageInfoFieldErrors | null;
  updateOffers: (params: TUpdateOffersParams) => void;
};

const useUpdateOffers = (initialOffers: TOffer[], isPaymentFailed: boolean): TUpdateOffersReturn => {
  const { t } = useTranslation();
  const setMessage = useMessage();
  const { setFieldLoading, isFieldLoading } = useOrderFieldsLoading();

  const isPackageSizesLoading = isFieldLoading(OrderFields.PACKAGE_SIZE);

  const [{ offers, coupon, errorMessage, fieldErrors }, setData] = useState<TUpdateOffersData>({
    offers: initialOffers,
    coupon: null,
    errorMessage: null,
    fieldErrors: null,
  });

  const { updateNonce, getCurrentNonce } = useNonce();

  const updateOffers = useCallback(
    ({
      countryFrom,
      countryTo,
      deliveryMethod,
      packageSizeTitle,
      promoCode,
      senderZipCode,
      receiverZipCode,
      affiliateId,
      shipmentId,
      senderEmail,
      senderPhoneNumber,
      offerGroupId,
    }: TUpdateOffersParams) => {
      const localNonce = updateNonce();

      if (!countryFrom || !countryTo || !packageSizeTitle) {
        setData((oldData) => ({ ...oldData, offers: [] }));
        setFieldLoading(OrderFields.DELIVERY_METHOD, false);
        setFieldLoading(OrderFields.PROMO_CODE, false);
        setFieldLoading(OrderFields.OFFER_GROUP_ID, false);
        return;
      }

      setFieldLoading(OrderFields.DELIVERY_METHOD, true);
      setFieldLoading(OrderFields.OFFER_GROUP_ID, true);
      setFieldLoading(OrderFields.PROMO_CODE, !!promoCode);

      // If promoCode is removing, clearing coupon early
      if (!promoCode) {
        setData((data) => ({ ...data, coupon: null }));
      }
      debouncedOffersLoading(async () => {
        if (localNonce !== getCurrentNonce()) {
          return;
        }
        if (isPackageSizesLoading) {
          return;
        }

        const {
          isSuccess,
          offers: newOffers,
          coupon: newCoupon,
          errorMessage: newErrorMessage,
          fieldErrors: newFieldErrors,
        } = await loadOffers({
          countryFrom,
          countryTo,
          packageSizeTitle,
          promoCode,
          senderZipCode,
          receiverZipCode,
          affiliateId,
          shipmentId,
          senderEmail,
          senderPhoneNumber,
          offerGroupId,
          deliveryMethod,
        });
        setData({
          offers: newOffers || [],
          coupon: newCoupon || null,
          errorMessage: newErrorMessage || null,
          fieldErrors: newFieldErrors || null,
        });

        setFieldLoading(OrderFields.DELIVERY_METHOD, false);
        setFieldLoading(OrderFields.OFFER_GROUP_ID, false);
        setFieldLoading(OrderFields.PROMO_CODE, false);

        const selectedOffer = newOffers?.find(
          ({ deliveryMethod: offerDeliveryMethod }) => offerDeliveryMethod === deliveryMethod
        );

        if (!isSuccess) {
          setMessage({
            message: t("order-message-failed_to_load_offers"),
            props: {
              variant: "error",
              icon: <Icon icon="warning" size="1.45rem" />,
              canClose: true,
              padding: "0.45rem 0.75rem",
            },
            zone: ORDER_MESSAGE_ZONE,
          });
          scrollToTheTop();
          return;
        }

        if (!isPaymentFailed && newOffers && newOffers.length > 0 && selectedOffer) {
          setMessage({
            message: null,
            props: {},
            zone: ORDER_MESSAGE_ZONE,
          });
        }

        if (!newOffers?.length) {
          setMessage({
            message: t("order-message-no_offers_available"),
            props: {
              variant: "error",
              icon: <Icon icon="warning" size="1.45rem" />,
              canClose: true,
              padding: "0.45rem 0.75rem",
            },
            zone: ORDER_MESSAGE_ZONE,
          });
          scrollToTheTop();
        }
      });
    },
    [updateNonce, setFieldLoading, getCurrentNonce, isPackageSizesLoading, setMessage, t]
  );

  return { offers, coupon, errorMessage, fieldErrors, updateOffers };
};
export default useUpdateOffers;
